import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../ui/button";
import { Sparkles, Loader2, Repeat, User, FileDown } from "lucide-react";
import { API } from "../../../App";
import { formatDateSafe } from "../../../lib/formatDate";

/** Vue côte à côte des analyses propres des deux partenaires + boucle interactionnelle IA. */
export const CoupleCompare = ({ report, analysisKey, participants, Form, specs, config, spec, onUpdated, lang }) => {
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const entries = participants.map((p) => ({ p, entry: report.analyses_by_patient?.[p.user_id]?.[analysisKey] }));
  const ready = entries.every((e) => e.entry?.data);
  const loop = report.couple_loops?.[analysisKey];

  const generateLoop = async () => {
    if (loop && !window.confirm("Remplacer la boucle interactionnelle actuelle ?")) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/session-reports/${report.report_id}/analyses/${analysisKey}/couple-loop`, {}, { withCredentials: true, params: lang ? { lang } : {} });
      onUpdated(r.data.report); toast.success(`Boucle interactionnelle générée · ${r.data.route?.label || ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de génération IA"); }
    finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await axios.get(`${API}/session-reports/${report.report_id}/analyses/${analysisKey}/couple-loop/pdf`, { withCredentials: true, responseType: "blob", params: lang ? { lang } : {} });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `boucle-couple-${analysisKey}-${(report.session_date || "").slice(0, 10)}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error("Impossible de générer le PDF"); }
    finally { setPdfBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid={`couple-compare-${analysisKey}`}>
      <div className="grid gap-4 lg:grid-cols-2">
        {entries.map(({ p, entry }) => (
          <div key={p.user_id} className="rounded-xl border border-rose-200 dark:border-rose-900/50 overflow-hidden" data-testid={`compare-col-${p.user_id}`}>
            <div className="flex items-center justify-between px-3 py-2 bg-rose-50/70 dark:bg-rose-950/20 text-sm">
              <span className="inline-flex items-center gap-1.5 font-semibold text-rose-900 dark:text-rose-100"><User className="w-4 h-4" /> {p.first_name}</span>
              {entry?.generated_at && <span className="text-[11px] text-muted-foreground">IA · {formatDateSafe(entry.generated_at, { day: "2-digit", month: "2-digit", year: "numeric" })}</span>}
            </div>
            <div className="p-3 pointer-events-none select-text">
              {entry?.data ? <Form analysisKey={analysisKey} data={entry.data} onChange={() => {}} specs={specs} report={report} config={config} />
                : <p className="text-sm text-muted-foreground py-6 text-center" data-testid={`compare-empty-${p.user_id}`}>Aucune analyse propre à {p.first_name} — sélectionnez {p.first_name} puis « Générer avec l'IA ».</p>}
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-xl border p-4 space-y-3" data-testid={`couple-loop-${analysisKey}`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h4 className="font-heading font-semibold flex items-center gap-2"><Repeat className="w-4 h-4 text-rose-600" /> Boucle interactionnelle du couple</h4>
            <p className="text-xs text-muted-foreground">Mise en regard des analyses « {spec.label} » de {participants.map((p) => p.first_name).join(" et ")} : cycle déclencheur → réaction → effet sur l'autre, points miroirs, leviers.</p>
            {loop?.generated_at && <p className="text-[11px] text-muted-foreground mt-0.5" data-testid="couple-loop-meta">IA · {loop.model} · {formatDateSafe(loop.generated_at, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>}
          </div>
          <div className="flex gap-2">
            {loop && (
              <Button variant="outline" size="sm" onClick={downloadPdf} disabled={pdfBusy} title="PDF à remettre au couple (logos, mention RGPD)" className="gap-1.5" data-testid={`couple-loop-pdf-${analysisKey}`}>
                {pdfBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} PDF couple
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={generateLoop} disabled={busy || !ready} title={ready ? "Repérer la boucle avec l'IA" : "Générez d'abord l'analyse de chaque partenaire"} className="gap-1.5" data-testid={`couple-loop-generate-${analysisKey}`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {busy ? "Analyse…" : loop ? "Régénérer la boucle" : "Repérer la boucle (IA)"}
            </Button>
          </div>
        </div>
        {loop ? (
          <div className="grid gap-4 md:grid-cols-2 text-sm">
            <div className="md:col-span-2 prose prose-sm dark:prose-invert max-w-none" data-testid="couple-loop-text"><h5 className="text-rose-700 dark:text-rose-300 mb-1">Le cycle</h5><div dangerouslySetInnerHTML={{ __html: loop.loop }} /></div>
            <div className="prose prose-sm dark:prose-invert max-w-none"><h5 className="mb-1">Points miroirs</h5><div dangerouslySetInnerHTML={{ __html: loop.mirror }} /></div>
            <div className="prose prose-sm dark:prose-invert max-w-none"><h5 className="mb-1">Leviers thérapeutiques</h5><div dangerouslySetInnerHTML={{ __html: loop.levers }} /></div>
            {loop.caution && <p className="md:col-span-2 text-xs italic text-muted-foreground border-t pt-2">{loop.caution}</p>}
          </div>
        ) : <p className="text-sm text-muted-foreground" data-testid="couple-loop-empty">{ready ? "Aucune boucle décrite pour le moment." : `Générez d'abord l'analyse « ${spec.label} » pour chaque partenaire.`}</p>}
      </div>
    </div>
  );
};
